import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { fmtLongDay, fmtShortDay, fmtTime, toCoachDateKey } from '../lib/time';
import { alert, haptic } from '../lib/telegram';
import { useT } from '../lib/i18n';
import { api } from '../screens/client/api';

/**
 * Booking on a client's behalf.
 *
 * Some clients will never open an app — they text, or they mention it at
 * the end of a session. Without this those bookings live in your head,
 * and the app only covers part of the business.
 *
 * Their own rate and credits apply exactly as if they'd booked it. The
 * only difference is that minimum notice and the booking horizon don't:
 * those rules stop clients booking awkwardly, not the coach.
 */
export function BookForSheet({ clientId, name, at, onClose, onDone }: {
  clientId: string; name: string; onClose: () => void; onDone: () => void;
  /** Calendar → «Неделя», an empty time tapped: that day's times, this one chosen. */
  at?: string;
}) {
  const t = useT();
  const [types, setTypes] = useState<any[] | null>(null);
  const [typeId, setTypeId] = useState<string>('');
  const [slots, setSlots] = useState<any[]>([]);
  const [chosen, setChosen] = useState<string | null>(null);
  const [payWith, setPayWith] = useState<'credits' | 'cash' | 'gift'>('credits');
  const [giftReason, setGiftReason] = useState('');
  const [busy, setBusy] = useState(false);
  // «Каждую неделю»: a number of weeks, or every week up to a date.
  const [weekly, setWeekly] = useState(false);
  const [weeks, setWeeks] = useState(4);
  const [byDate, setByDate] = useState(false);
  const [until, setUntil] = useState('');
  const [series, setSeries] = useState<{ startsAt: string; free: boolean }[] | null>(null);
  // While new dates load, the old list stays and the button waits.
  const [seriesLoading, setSeriesLoading] = useState(false);
  const repeat = weekly && payWith !== 'gift' && !!chosen;
  const localDay = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  useEffect(() => {
    if (chosen && !until) setUntil(localDay(new Date(new Date(chosen).getTime() + 3 * 7 * 86400000)));
  }, [chosen]);
  useEffect(() => {
    if (!repeat) { setSeries(null); return; }
    let live = true;
    setSeriesLoading(true);
    const q = byDate ? `until=${until}` : `weeks=${weeks}`;
    api<{ dates: { startsAt: string; free: boolean }[] }>(
      `/api/coach/series-preview?session_type_id=${typeId}&starts_at=${encodeURIComponent(chosen!)}&${q}`)
      .then((r) => { if (live) setSeries(r.dates); }).catch(() => { if (live) setSeries([]); })
      .finally(() => { if (live) setSeriesLoading(false); });
    return () => { live = false; };
  }, [repeat, chosen, typeId, weeks, byDate, until]);
  const freeDates = (series ?? []).filter((d) => d.free).length;

  useEffect(() => {
    api<any[]>('/api/session-types').then((t) => {
      const priv = t.filter((x) => x.kind === 'private');
      setTypes(priv);
      setTypeId(priv[0]?.id ?? '');
    }).catch(() => setTypes([]));
  }, []);

  useEffect(() => {
    if (!typeId) return;
    const from = at ? toCoachDateKey(new Date(at)) : new Date().toISOString().slice(0, 10);
    const to = at ? from : new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10);
    api<any>(`/api/slots?session_type_id=${typeId}&from=${from}&to=${to}&for_client=${clientId}`)
      .then((r) => {
        setSlots(r.slots ?? []);
        const hit = at && (r.slots ?? []).find((sl: any) => new Date(sl.startsAt).getTime() === new Date(at).getTime());
        if (hit) setChosen(hit.startsAt);
      })
      .catch(() => setSlots([]));
    setChosen(null);
  }, [typeId]);

  const byDay = new Map<string, any[]>();
  for (const sl of slots) {
    const k = new Date(sl.startsAt).toISOString().slice(0, 10);
    if (!byDay.has(k)) byDay.set(k, []);
    byDay.get(k)!.push(sl);
  }

  async function book() {
    if (!chosen) return;
    setBusy(true);
    try {
      if (repeat) {
        const res = await api<{ booked: unknown[]; skipped: unknown[] }>('/api/coach/bookings/series', {
          method: 'POST',
          body: JSON.stringify({
            client_id: clientId, session_type_id: typeId, starts_at: chosen, payment_mode: payWith,
            ...(byDate ? { until } : { weeks }),
          }),
        });
        haptic.success();
        const n = res.booked.length, k = res.skipped.length;
        await alert(t('co.cd.seriesDone', { n, sessions: t.word('co.today.sessions', n) })
          + (k > 0 ? t('co.cd.seriesSkipped', { n: k }) : ''));
        onDone();
        onClose();
        return;
      }
      await api('/api/coach/bookings', {
        method: 'POST',
        body: JSON.stringify({
          client_id: clientId, session_type_id: typeId,
          starts_at: chosen, payment_mode: payWith,
          reason: payWith === 'gift' ? giftReason : undefined,
        }),
      });
      haptic.success();
      await alert(payWith === 'gift'
        ? t('co.cd.gifted')
        : t('co.cd.bookedFor'));
      onDone();
      onClose();
    } catch (err: any) {
      haptic.error();
      await alert(err.message === 'insufficient_credits'
        ? t('co.cd.noCreditsLeft', { name })
        : t('co.cd.couldNotBook', { message: err.message }));
    } finally { setBusy(false); }
  }

  return createPortal(
    <div className="sheet">
      <div className="sheet__panel">
        <div className="sheet__title">{t('co.cd.bookFor', { name })}</div>

        {types && types.length > 1 && (
          <div className="repeat__opts" style={{ marginTop: 10 }}>
            {types.map((ty) => (
              <button key={ty.id} className="chip" aria-pressed={typeId === ty.id}
                onClick={() => { haptic.tap(); setTypeId(ty.id); }}>
                {t('co.cd.minutes', { n: ty.duration_min })}
              </button>
            ))}
          </div>
        )}

        <div className="paybar" style={{ padding: '10px 0 4px' }}>
          <button className="paybar__opt" aria-pressed={payWith === 'credits'}
            onClick={() => { haptic.tap(); setPayWith('credits'); }}>{t('co.cd.useCredits')}</button>
          <button className="paybar__opt" aria-pressed={payWith === 'cash'}
            onClick={() => { haptic.tap(); setPayWith('cash'); }}>{t('co.cd.payPerSession')}</button>
          <button className="paybar__opt" aria-pressed={payWith === 'gift'}
            onClick={() => { haptic.tap(); setPayWith('gift'); }}>{t('co.cd.gift')}</button>
        </div>
        {payWith === 'gift' && (
          <input className="paybar__opt" style={{ width: '100%', textAlign: 'left', marginTop: 8 }}
            placeholder={t('co.cd.giftWhy')}
            value={giftReason} onChange={(e) => setGiftReason(e.target.value)} />
        )}

        <div className="sheet__label">{t('co.cd.pickTime')}</div>
        {slots.some((sl) => sl.outside) && <div className="card__when outside-hint">{t('co.cd.outsideHint')}</div>}
        <div style={{ maxHeight: '38vh', overflowY: 'auto' }}>
          {slots.length === 0 ? (
            <div className="card__when">{t('co.cd.nothingOpen')}</div>
          ) : (
            [...byDay.entries()].map(([day, list]) => (
              <div key={day} style={{ marginBottom: 10 }}>
                <div className="card__when" style={{ marginBottom: 4 }}>
                  {fmtLongDay(new Date(day + 'T12:00:00'))}
                </div>
                <div className="repeat__opts" style={{ flexWrap: 'wrap' }}>
                  {list.map((sl) => (
                    <button key={sl.startsAt} className={`chip${sl.outside ? ' chip--outside' : ''}`}
                      aria-pressed={chosen === sl.startsAt}
                      aria-label={sl.outside ? `${fmtTime(sl.startsAt)} · ${t('co.cd.outside')}` : undefined}
                      onClick={() => { haptic.tap(); setChosen(sl.startsAt); }}>
                      {fmtTime(sl.startsAt)}
                    </button>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>

        {chosen && payWith !== 'gift' && (
          <div className="repeat__opts series-opts" style={{ marginTop: 10 }}>
            <button className="chip" aria-pressed={!weekly} onClick={() => { haptic.tap(); setWeekly(false); }}>{t('co.cd.once')}</button>
            <button className="chip" aria-pressed={weekly} onClick={() => { haptic.tap(); setWeekly(true); }}>{t('co.cd.weekly')}</button>
          </div>
        )}
        {repeat && (
          <div className="series">
            <div className="series__count">
              <button className="chip" aria-pressed={!byDate} onClick={() => { haptic.tap(); setByDate(false); }}>
                {t('co.cd.weeksN', { n: weeks })}
              </button>
              {!byDate && (
                <span className="series__step">
                  <button className="chip" aria-label="−" disabled={weeks <= 2} onClick={() => setWeeks(weeks - 1)}>−</button>
                  <button className="chip" aria-label="+" disabled={weeks >= 12} onClick={() => setWeeks(weeks + 1)}>+</button>
                </span>
              )}
              <button className="chip" aria-pressed={byDate} onClick={() => { haptic.tap(); setByDate(true); }}>{t('co.cd.untilDate')}</button>
              {byDate && (
                <input type="date" className="series__date" value={until} min={localDay(new Date(chosen!))}
                  onChange={(e) => setUntil(e.target.value)} />
              )}
            </div>
            {series === null ? <div className="skeleton" style={{ height: 60 }} /> : (
              <ul className="series__list">
                {series.map((d) => (
                  <li key={d.startsAt} className={d.free ? '' : 'series__taken'}>
                    <span>{fmtShortDay(new Date(d.startsAt))}, {fmtTime(d.startsAt)}</span>
                    {!d.free && <em>{t('co.cd.takenSkip')}</em>}
                  </li>
                ))}
              </ul>
            )}
            <div className="card__when">{t('co.cd.seriesPay')}</div>
          </div>
        )}
        {chosen && slots.find((sl) => sl.startsAt === chosen)?.outside && (
          <div className="card__when outside-hint" style={{ marginTop: 8 }}>{t('co.cd.outsideChosen', { time: fmtTime(chosen) })}</div>
        )}
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <button className="paybar__opt" data-back style={{ flex: 1 }} onClick={onClose}>{t('co.cd.back')}</button>
          <button className="paybar__opt"
            style={{ flex: 2, background: 'var(--accent)', color: 'var(--accent-text)', borderColor: 'var(--accent)' }}
            disabled={!chosen || busy || (repeat && (series === null || seriesLoading || freeDates < 2))} onClick={book}>
            {busy ? t('co.cd.booking')
              : repeat && series === null ? t('co.cd.weekly')
              : repeat ? t('co.cd.bookN', { n: freeDates, sessions: t.word('co.today.sessions', freeDates) })
              : chosen ? t('co.cd.bookAt', { time: fmtTime(chosen) }) : t('co.cd.pickTime')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
